import { MaterialReactTable } from 'material-react-table';
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import KimaruButton from '../buttons/KimaruButton';
import DeferButton from '../buttons/DeferButton';
import AcceptButton from '../buttons/AcceptButton';
import UndoButton from '../buttons/UndoButton';
import { useSelector } from "react-redux";

const MOVEMENT_API_BASE = 'https://api.stag.kimaru.ai/api/v1/movement_recommendation';
const PRICE_API_BASE = 'https://api.stag.kimaru.ai/api/v1/price_recommendation';

const InventoryAccordionTable = ({ containerData, onOpenAgenticDrawer, location, overriddenRecommendations = new Set(), onMarkOverridden, onUnmarkOverridden }) => {
    const [rowSelection, setRowSelection] = useState({});
    const [acceptedRecommendations, setAcceptedRecommendations] = useState(new Set());
    const [deferredRecommendations, setDeferredRecommendations] = useState(new Set());
    const [deferringIds, setDeferringIds] = useState(new Set());
    const [loading, setLoading] = useState(false);
    const [recommendationActions, setRecommendationActions] = useState(new Map()); // Track action type: 'accept' | 'defer' | 'kimaru'

    const userEmail = useSelector((state) => state.auth?.user?.email);
    const isSamUser = userEmail?.trim()?.toLowerCase() === "sam@kimaru.ai";

    const locationToCheck = location || containerData?.location;
    const isHeatWarningLocation = locationToCheck && ["Miyazaki", "Osaka"].includes(locationToCheck);
    const isSpoilageAlert = !isHeatWarningLocation;

    // Fetch accepted and deferred recommendations on mount
    useEffect(() => {
        const fetchRecommendationStates = async () => {
            try {
                setLoading(true);
                const requests = [
                    fetch(`${MOVEMENT_API_BASE}/get_accepted_recommendations`, {
                        headers: { 'email': userEmail },
                    }),
                    fetch(`${MOVEMENT_API_BASE}/get_deferred_recommendations`, {
                        headers: { 'email': userEmail },
                    })
                ];

                if (!isSamUser) {
                    requests.push(
                        fetch(`${PRICE_API_BASE}/get_accepted_recommendations`),
                        fetch(`${PRICE_API_BASE}/get_deferred_recommendations`)
                    );
                }

                const responses = await Promise.all(requests);
                const dataArray = await Promise.all(
                    responses.map(res => res.json())
                );

                const acceptedIds = [
                    ...(dataArray[0].accepted_recommendations?.map(rec => rec.rec_id) || []),
                    ...(dataArray[2]?.accepted_recommendations?.map(rec => rec.rec_id) || [])
                ];

                const deferredIds = [
                    ...(dataArray[1].deferred_recommendations?.map(rec => rec.rec_id) || []),
                    ...(dataArray[3]?.deferred_recommendations?.map(rec => rec.rec_id) || [])
                ];

                setAcceptedRecommendations(new Set(acceptedIds));
                setDeferredRecommendations(new Set(deferredIds));
            } catch (error) {
                console.error('Error fetching recommendation states:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchRecommendationStates();
    }, [userEmail, isSamUser]);

    const calculateOrderFulfillmentRisk = useCallback((quantityOnHand, dailySales, avgDailySales, typhoon) => {
        if (!quantityOnHand || !dailySales || !avgDailySales || dailySales === 0 || avgDailySales === 0) {
            return null;
        }

        const daysUntilStockout = quantityOnHand / dailySales;
        const targetDays = 14;
        const stockoutComponent = Math.max(0, 1 - Math.min(daysUntilStockout / targetDays, 1));
        const volatility = Math.abs(dailySales - avgDailySales) / avgDailySales;
        const volatilityComponent = Math.min(volatility, 1);
        const externalRisk = typhoon ? 1 : 0;
        const riskScore = (0.5 * stockoutComponent) + (0.3 * volatilityComponent) + (0.2 * externalRisk);

        return Math.min(riskScore * 100, 100);
    }, []);

    const groupedProducts = useMemo(() => {
        if (!containerData?.recommendations) return [];
        const stablePercentFromId = (id) => {
            if (!id) return Math.floor(Math.random() * 100) + 1;
            let h = 0;
            for (let i = 0; i < id.length; i++) {
                h = ((h << 5) - h) + id.charCodeAt(i);
                h |= 0;
            }
            const positive = Math.abs(h);
            return (positive % 100) + 1;
        };

        const groups = {};
        containerData.recommendations.forEach(rec => {
            const recId = rec.rec_id || rec.recommendation?.id || `${rec.sku_id || ''}-${rec.item_name || ''}`;
            if (rec.predicted_reduction === undefined) {
                rec.predicted_reduction = stablePercentFromId(String(recId));
            }
            const sku = rec.sku_id;
            if (!groups[sku]) {
                groups[sku] = {
                    id: sku,
                    productName: rec.item_name,
                    image: rec.item_image_url,
                    sku: sku,
                    daysToExpiry: rec.days_to_expiry,
                    quantityOnHand: rec.quantity_on_hand,
                    avgDailySales: rec.avg_daily_sales,
                    sellThroughRate: rec.sell_through_rate,
                    stockoutRisk: rec.stockout_risk,
                    recommendations: [rec],
                    type: rec.recommendation_type
                };
            } else {
                groups[sku].recommendations.push(rec);
                if (rec.days_to_expiry !== undefined) groups[sku].daysToExpiry = rec.days_to_expiry;
                if (rec.quantity_on_hand !== undefined) groups[sku].quantityOnHand = rec.quantity_on_hand;
                if (rec.avg_daily_sales !== undefined) groups[sku].avgDailySales = rec.avg_daily_sales;
                if (rec.sell_through_rate !== undefined) groups[sku].sellThroughRate = rec.sell_through_rate;
                if (rec.stockout_risk !== undefined) groups[sku].stockoutRisk = rec.stockout_risk;
            }
        });

        let productsArray = Object.values(groups);

        // Precompute a stable percentage/confidence per product so it doesn't change on re-renders
        productsArray = productsArray.map(row => {
            const rate = row.sellThroughRate;
            let computedPercentage = null;
            if (rate !== undefined && rate !== null) {
                const baseValue = Math.round(rate * 100);
                const randomHighValue = Math.floor(Math.random() * 8) * 3 + 82;
                computedPercentage = baseValue < 80 ? randomHighValue : Math.min(baseValue, 100);
            }
            return { ...row, computedPercentage };
        });

        if (isSamUser) {
            productsArray.sort((a, b) => {
                const getRiskScore = (product) => {
                    const firstRec = product.recommendations[0];
                    const quantityOnHand = product.quantityOnHand ?? firstRec?.quantity_on_hand;
                    const dailySales = firstRec?.daily_sales;
                    const avgDailySales = product.avgDailySales ?? firstRec?.avg_daily_sales;
                    const typhoon = firstRec?.typhoon;
                    return calculateOrderFulfillmentRisk(quantityOnHand, dailySales, avgDailySales, typhoon);
                };

                const riskA = getRiskScore(a);
                const riskB = getRiskScore(b);

                if (riskA === null && riskB === null) return 0;
                if (riskA === null) return 1;
                if (riskB === null) return -1;

                return riskB - riskA;
            });
        }

        return productsArray;
    }, [containerData, isSamUser, calculateOrderFulfillmentRisk]);

    const handleAcceptRecommendation = useCallback(async (recommendationId, recommendationType) => {
        try {
            const endpoint = recommendationType === 'movement'
                ? `${MOVEMENT_API_BASE}/accept_recommendation`
                : `${PRICE_API_BASE}/accept_recommendation`;

            const response = await fetch(endpoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'user-email': userEmail },
                body: JSON.stringify({ recommendation_id: recommendationId })
            });

            if (response.ok) {
                setAcceptedRecommendations(prev => new Set([...prev, recommendationId]));
                setRecommendationActions(prev => new Map(prev).set(recommendationId, 'accept'));
            } else {
                throw new Error('Failed to accept recommendation');
            }
        } catch (error) {
            console.error('Error accepting recommendation:', error);
        }
    }, [userEmail]);

    const handleDeferRecommendation = useCallback(async (recommendationId, recommendationType) => {
        const isBulk = Array.isArray(recommendationId);

        try {
            if (isBulk) {
                // Mark all ids as deferring
                setDeferringIds(prev => new Set([...Array.from(prev), ...recommendationId]));

                const results = await Promise.all(recommendationId.map(async (id) => {
                    try {
                        const endpointBase = recommendationType === 'movement' ? MOVEMENT_API_BASE : PRICE_API_BASE;
                        const endpoint = `${endpointBase}/defer_recommendation`;

                        const response = await fetch(endpoint, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json', 'user-email': userEmail },
                            body: JSON.stringify({ recommendation_id: id })
                        });

                        return { id, ok: response.ok };
                    } catch (err) {
                        console.error('Error deferring recommendation:', err);
                        return { id, ok: false };
                    }
                }));

                const succeeded = results.filter(r => r.ok).map(r => r.id);
                if (succeeded.length > 0) {
                    setDeferredRecommendations(prev => new Set([...Array.from(prev), ...succeeded]));
                    setRecommendationActions(prev => {
                        const next = new Map(prev);
                        succeeded.forEach(id => next.set(id, 'defer'));
                        return next;
                    });
                }

                // Clear deferring ids
                setDeferringIds(prev => {
                    const next = new Set(prev);
                    recommendationId.forEach(id => next.delete(id));
                    return next;
                });
            } else {
                // Single id
                setDeferringIds(prev => new Set([...Array.from(prev), recommendationId]));

                const endpointBase = recommendationType === 'movement' ? MOVEMENT_API_BASE : PRICE_API_BASE;
                const endpoint = `${endpointBase}/defer_recommendation`;

                const response = await fetch(endpoint, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'user-email': userEmail },
                    body: JSON.stringify({ recommendation_id: recommendationId })
                });

                if (response.ok) {
                    setDeferredRecommendations(prev => new Set([...Array.from(prev), recommendationId]));
                    setRecommendationActions(prev => new Map(prev).set(recommendationId, 'defer'));
                } else {
                    const errorText = await response.text().catch(() => null);
                    console.error('Failed to defer recommendation', errorText);
                }

                // Clear deferring id
                setDeferringIds(prev => {
                    const next = new Set(prev);
                    next.delete(recommendationId);
                    return next;
                });
            }
        } catch (error) {
            console.error('Error deferring recommendation:', error);
            // Best-effort cleanup
            if (isBulk) {
                setDeferringIds(prev => {
                    const next = new Set(prev);
                    recommendationId.forEach(id => next.delete(id));
                    return next;
                });
            } else {
                setDeferringIds(prev => {
                    const next = new Set(prev);
                    next.delete(recommendationId);
                    return next;
                });
            }
        }
    }, [userEmail]);

    const calculateInStockDays = (quantityOnHand, avgDailySales) => {
        if (!quantityOnHand || !avgDailySales || avgDailySales === 0) return 'N/A';
        return Math.round(quantityOnHand / avgDailySales);
    };

    const handleUndoRecommendation = useCallback((recommendationId, actionType) => {
        // Local undo: do not call backend "undo" endpoints. Update UI state only.
        try {
            if (actionType === 'accept') {
                setAcceptedRecommendations(prev => {
                    const next = new Set(prev);
                    next.delete(recommendationId);
                    return next;
                });
            } else if (actionType === 'defer') {
                setDeferredRecommendations(prev => {
                    const next = new Set(prev);
                    next.delete(recommendationId);
                    return next;
                });
            }

            // Remove from action tracking so the item returns to default state
            setRecommendationActions(prev => {
                const next = new Map(prev);
                next.delete(recommendationId);
                return next;
            });

            // If it was overridden by Kimaru, notify parent to remove it from the overridden set
            try {
                if (typeof onUnmarkOverridden === 'function') {
                    onUnmarkOverridden(recommendationId);
                }
            } catch (err) {
                console.warn('onUnmarkOverridden failed', err);
            }
        } catch (error) {
            console.error('Error performing local undo:', error);
        }
    }, []);

    const formatStockoutRisk = (risk) => {
        if (!risk) return 'N/A';
        return risk.charAt(0).toUpperCase() + risk.slice(1).toLowerCase();
    };

    const AlertIcon = ({ isDestructive }) => (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" style={{ width: '20px', height: '20px', flexShrink: 0 }}>
            <path d="M10.0007 7.49988V10.8332M10.0007 14.1665H10.0091M18.109 14.9999L11.4423 3.3332C11.297 3.0767 11.0862 2.86335 10.8314 2.71492C10.5767 2.56649 10.2872 2.48828 9.99234 2.48828C9.69752 2.48828 9.40797 2.56649 9.15324 2.71492C8.8985 2.86335 8.6877 3.0767 8.54234 3.3332L1.87567 14.9999C1.72874 15.2543 1.6517 15.5431 1.65235 15.837C1.653 16.1308 1.73132 16.4192 1.87938 16.673C2.02744 16.9269 2.23996 17.137 2.49542 17.2822C2.75088 17.4274 3.04018 17.5025 3.33401 17.4999H16.6673C16.9598 17.4996 17.2469 17.4223 17.5001 17.2759C17.7532 17.1295 17.9634 16.9191 18.1094 16.6658C18.2555 16.4125 18.3324 16.1252 18.3323 15.8328C18.3322 15.5404 18.2552 15.2531 18.109 14.9999Z"
                stroke={isDestructive ? '#DC2626' : '#FB923D'}
                strokeWidth="1.33"
                strokeLinecap="round"
                strokeLinejoin="round" />
        </svg>
    );

    const mainColumns = useMemo(() => {
        const baseColumns = [
            {
                accessorKey: 'recommendations',
                header: 'Recommendations',
                size: 80,
                Cell: ({ row }) => (
                    <span className="text-sm">{row.original.recommendations.length} Recommendations</span>
                )
            },
            {
                accessorKey: 'productName',
                header: isSamUser ? 'Component Name / Part' : 'Product & SKU',
                size: 300,
                Cell: ({ row }) => (
                    <div className="flex items-center gap-2 py-2">
                        <img
                            src={row.original.image}
                            alt={row.original.productName}
                            className="w-8 h-8 object-cover rounded"
                            onError={(e) => e.target.src = 'https://via.placeholder.com/32'}
                        />
                        <div className="flex flex-col">
                            <span className="text-sm leading-tight">{row.original.productName}</span>
                            <span className="text-xs text-gray-500 leading-tight font-light">{row.original.sku}</span>
                        </div>
                    </div>
                ),
            },
            {
                accessorKey: 'column1',
                header: isSamUser ? 'Current Stock' : (isSpoilageAlert ? 'Expiry Days' : 'Current Stock'),
                size: 120,
                Cell: ({ row }) => {
                    const value = isSamUser ? row.original.quantityOnHand : (isSpoilageAlert ? row.original.daysToExpiry : row.original.quantityOnHand);
                    const formattedValue = value?.toLocaleString();
                    const showAlert = isSpoilageAlert && value !== undefined && value <= 4;
                    const isDestructive = isSpoilageAlert && value <= 3;
                    const colorClass = showAlert ? (isDestructive ? 'text-red-600' : 'text-orange-400') : '';

                    return (
                        <div className="flex items-center gap-2">
                            {showAlert && <AlertIcon isDestructive={isDestructive} />}
                            <span className={`text-sm ${showAlert ? colorClass : ''}`}>
                                {formattedValue !== undefined ? formattedValue : 'N/A'}
                            </span>
                        </div>
                    );
                }
            },
            {
                accessorKey: 'column2',
                header: isSamUser ? 'Order Backlog' : (isSpoilageAlert ? 'In Stock (days)' : 'Demand Forecast'),
                size: 120,
                Cell: ({ row }) => {
                    if (isSamUser) {
                        const quantityToOrder = Number(
                            row.original.recommendations?.[0]?.recommendation?.quantity_to_order ?? 0
                        ).toLocaleString();
                        return <span className="text-sm">{quantityToOrder !== undefined ? quantityToOrder : 'N/A'}</span>;
                    } else if (isSpoilageAlert) {
                        const inStockDays = calculateInStockDays(row.original.quantityOnHand, row.original.avgDailySales);
                        const numericValue = typeof inStockDays === 'number' ? inStockDays : parseFloat(inStockDays);
                        const formattedValue = !isNaN(numericValue) ? numericValue.toLocaleString() : 'N/A';
                        const showAlert = !isNaN(numericValue) && numericValue <= 4;
                        const isDestructive = numericValue <= 3;
                        const colorClass = isDestructive ? 'text-red-600' : 'text-orange-400';

                        return (
                            <div className="flex items-center gap-2">
                                {showAlert && <AlertIcon isDestructive={isDestructive} />}
                                <span className={`text-sm ${showAlert ? colorClass : ''}`}>
                                    {formattedValue}
                                </span>
                            </div>
                        );
                    } else {
                        return <span className="text-sm">{formatStockoutRisk(row.original.stockoutRisk)}</span>;
                    }
                }
            },
            {
                accessorKey: 'column3',
                header: isSamUser ? 'Order Fulfillment Risk' : 'Units On Hand',
                size: 120,
                Cell: ({ row }) => {
                    if (isSamUser) {
                        const firstRec = row.original.recommendations[0];
                        const quantityOnHand = row.original.quantityOnHand ?? firstRec?.quantity_on_hand;
                        const dailySales = firstRec?.daily_sales;
                        const avgDailySales = row.original.avgDailySales ?? firstRec?.avg_daily_sales;
                        const typhoon = firstRec?.typhoon;

                        const riskScore = calculateOrderFulfillmentRisk(quantityOnHand, dailySales, avgDailySales, typhoon);

                        if (riskScore === null) {
                            return <span className="text-sm text-gray-500">N/A</span>;
                        }

                        const showAlert = riskScore >= 40;
                        const isDestructive = riskScore > 79;

                        return (
                            <div className="flex items-center gap-2">
                                {showAlert && <AlertIcon isDestructive={isDestructive} />}
                                <span className="text-sm font-medium text-black">
                                    {riskScore.toFixed(0)}%
                                </span>
                            </div>
                        );
                    }
                    return <span className="text-sm">{row.original.quantityOnHand ?? 'N/A'}</span>;
                }
            },
            {
                accessorKey: 'sellThroughRate',
                header: isSamUser ? 'Confidence' : 'Sell-Through Forecast (%)',
                size: 140,
                Cell: ({ row }) => {
                    const percentage = row.original.computedPercentage;
                    return <span className="text-sm">{percentage !== undefined && percentage !== null ? `${percentage}%` : 'N/A'}</span>;
                }
            },
            {
                accessorKey: 'actions',
                header: 'Actions',
                size: 180,
                Cell: ({ row }) => {
                    // Check if all recommendations in this group are deferred
                    const recs = Array.isArray(row.original.recommendations) ? row.original.recommendations : [];
                    const recIds = recs.map(r => r.recommendation?.id || r.rec_id).filter(Boolean);
                    const allDeferred = recIds.length > 0 && recIds.every(id => deferredRecommendations.has(id));
                    const anyDeferring = recIds.some(id => deferringIds.has(id));

                    return (
                        <div className="flex gap-2">
                            <KimaruButton
                                animated={true}
                                isKimaru={false}
                                onClick={() => {
                                    try {
                                        onOpenAgenticDrawer && onOpenAgenticDrawer(recIds, containerData?.container_id, 'product');
                                    } catch (err) {
                                        console.error('Failed to open agentic drawer for SKU:', err);
                                        onOpenAgenticDrawer && onOpenAgenticDrawer([]);
                                    }
                                }}
                            />

                            <DeferButton
                                isDeferred={allDeferred}
                                onClick={() => {
                                    try {
                                        if (recs.length === 0) return;

                                        // Group IDs by recommendation_type
                                        const byType = recs.reduce((acc, r) => {
                                            const id = r.recommendation?.id || r.rec_id;
                                            const t = r.recommendation_type || row.original.type || 'movement';
                                            if (!id) return acc;
                                            if (!acc[t]) acc[t] = [];
                                            acc[t].push(id);
                                            return acc;
                                        }, {});

                                        Object.entries(byType).forEach(([type, ids]) => {
                                            if (Array.isArray(ids) && ids.length > 0) {
                                                handleDeferRecommendation(ids, type);
                                            }
                                        });
                                    } catch (err) {
                                        console.error('Failed to defer group recommendations:', err);
                                    }
                                }}
                                disabled={anyDeferring || allDeferred}
                            />
                        </div>
                    );
                }
            }
        ];

        return baseColumns;
    }, [isSpoilageAlert, isSamUser, deferredRecommendations, deferringIds]);

    const subGridColumns = [
        {
            accessorKey: 'recommendation.id',
            header: 'Recommendation ID',
            size: 120,
            Cell: ({ cell }) => <span className="text-sm">{cell.getValue()}</span>
        },
        {
            accessorKey: 'recommendation.description',
            header: 'Recommendation',
            size: 280,
            Cell: ({ cell }) => <span className="text-sm">{cell.getValue()}</span>
        },
        {
            accessorKey: 'predictedOutcome',
            header: 'Predicted Outcome',
            size: 150,
            Cell: ({ row }) => {
                if (isSamUser) {
                    const percent = row.original.predicted_reduction ?? row.original.predictedReduction ?? null;
                    const display = percent !== null ? `${percent}%` : 'N/A';
                    return (
                        <div className="flex items-center gap-1 text-sm text-black">
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M8.00004 3.33398V12.6673M8.00004 12.6673L12.6667 8.00065M8.00004 12.6673L3.33337 8.00065" stroke="#059669" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                            Reduce fulfillment risk by {display}
                        </div>
                    );
                }

                const type = row.original.recommendation_type;

                if (type === 'movement') {
                    return (
                        <div className="flex items-center gap-1 text-sm text-black">
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path
                                    d="M10.6667 10.6667L12 12.0001L14.6667 9.33341M14 6.66668V5.33335C13.9998 5.09953 13.938 4.86989 13.821 4.66746C13.704 4.46503 13.5358 4.29692 13.3333 4.18002L8.66667 1.51335C8.46397 1.39633 8.23405 1.33472 8 1.33472C7.76595 1.33472 7.53603 1.39633 7.33333 1.51335L2.66667 4.18002C2.46418 4.29692 2.29599 4.46503 2.17897 4.66746C2.06196 4.86989 2.00024 5.09953 2 5.33335V10.6667C2.00024 11.1301 2.06196 11.3326 2.17897 11.535C2.29599 11.7031 2.46418 11.82 2.66667 11.82L7.33333 14.4867C7.53603 14.6037 7.76595 14.6653 8 14.6653C8.23405 14.6653 8.46397 14.6037 8.66667 14.4867L10 13.7267M5 2.84676L11 6.28009M2.19336 4.66675L8.00003 8.00008M8.00003 8.00008L13.8067 4.66675M8.00003 8.00008L8 14.6667"
                                    stroke="#16A34A"
                                    strokeWidth="1.33"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                            Prevent Stockout
                        </div>
                    );
                } else if (type === 'price') {
                    const transform = row.original.recommendation.strategy === 'markdown' ? 'rotate(180deg)' : 'none';
                    return (
                        <div className="flex items-center gap-1 text-sm text-black">
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ transform }}>
                                <path d="M3.33203 7.99967L7.9987 3.33301M7.9987 3.33301L12.6654 7.99967M7.9987 3.33301V12.6663" stroke="#16A34A" strokeWidth="1.33" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                            {row.original.recommendation.strategy === 'markdown' ? 'Increase Sales' : 'Increase Profit'}
                        </div>
                    );
                }
                return null;
            }
        },
        ...(isHeatWarningLocation ? [{
            accessorKey: 'confidence',
            header: 'Confidence',
            size: 120,
            Cell: () => <span className="text-sm">-</span>
        }] : []),
        {
            accessorKey: 'actions',
            header: 'Actions',
            size: 180,
            Cell: ({ row }) => {
                const recommendationId = row.original.recommendation.id;
                const isAccepted = acceptedRecommendations.has(recommendationId);
                const isDeferred = deferredRecommendations.has(recommendationId);
                const isDeferring = deferringIds.has(recommendationId);
                const type = row.original.recommendation_type;
                const actionType = recommendationActions.get(recommendationId);

                // Priority: accepted -> deferred -> kimaru (override) -> default (show all)
                const isKimaru = overriddenRecommendations && overriddenRecommendations.has && overriddenRecommendations.has(recommendationId);

                if (isAccepted && actionType === 'accept') {
                    return (
                        <div className="flex gap-2">
                            <AcceptButton isAccepted={true} disabled={true} />
                            <UndoButton onClick={() => handleUndoRecommendation(recommendationId, 'accept')} />
                        </div>
                    );
                }

                if (isDeferred && actionType === 'defer') {
                    return (
                        <div className="flex gap-2">
                            <DeferButton isDeferred={true} disabled={true} />
                            <UndoButton onClick={() => handleUndoRecommendation(recommendationId, 'defer')} />
                        </div>
                    );
                }

                if (isKimaru) {
                    return (
                        <div className="flex gap-2">
                            <KimaruButton animated={false} isKimaru={true} disabled={true} onClick={() => handleKimaruClick(row.original)} />
                            <UndoButton onClick={() => handleUndoRecommendation(recommendationId, 'kimaru')} />
                        </div>
                    );
                }

                // Default: show all three buttons
                return (
                    <div className="flex gap-2">
                        <AcceptButton
                            isAccepted={isAccepted}
                            onClick={() => handleAcceptRecommendation(recommendationId, type)}
                            disabled={isAccepted || isDeferred}
                        />

                        <KimaruButton
                            animated={true}
                            isKimaru={false}
                            onClick={() => handleKimaruClick(row.original)}
                            disabled={isAccepted || isDeferred}
                        />

                        <DeferButton
                            isDeferred={isDeferred}
                            onClick={() => handleDeferRecommendation(recommendationId, type)}
                            disabled={isDeferring || isAccepted || isDeferred}
                        />
                    </div>
                );
            }
        }
    ];

    const handleKimaruClick = useCallback((recommendation) => {
        onOpenAgenticDrawer(recommendation);
    }, [onOpenAgenticDrawer]);

    return (
        <div className="mx-auto mb-6">
            {loading && (
                <div className="flex justify-center py-4">
                    <span className="text-sm text-gray-500">Loading recommendation states...</span>
                </div>
            )}

            <MaterialReactTable
                columns={mainColumns}
                data={groupedProducts}
                enableExpanding={true}
                getCanExpand={() => true}
                muiExpandButtonProps={({ row }) => ({
                    sx: {
                        transform: row.getIsExpanded() ? 'rotate(180deg)' : 'rotate(-90deg)',
                        transition: 'transform 0.2s ease-in-out',
                        '& .MuiIconButton-root': {
                            padding: '0px',
                        }
                    }
                })}
                muiTableBodyRowProps={() => ({
                    sx: {
                        cursor: 'pointer',
                        height: '0px',
                        backgroundColor: '#ffffff',
                        '&:hover': {
                            backgroundColor: '#f8f9fa',
                        }
                    },
                })}
                muiTableProps={{
                    sx: {
                        '& .MuiTableRow-root': {
                            borderSpacing: '0',
                        },
                    }
                }}
                muiTableContainerProps={{
                    sx: {
                        '& .MuiTable-root': {
                            borderCollapse: 'collapse',
                            borderSpacing: '0',
                        }
                    }
                }}
                state={{ rowSelection }}
                onRowSelectionChange={setRowSelection}
                enableHiding={false}
                enableDensityToggle={false}
                enableTopToolBar={false}
                enableColumnActions={false}
                enableSorting={false}
                enableGlobalFilter={false}
                enableColumnFilters={false}
                enableFullScreenToggle={false}
                muiTableHeadProps={{
                    sx: {
                        'th .Mui-TableHeadCell-Content-Labels, th .Mui-TableHeadCell-Content-Wrapper': {
                            width: '100%',
                        },
                        'th:last-child': {
                            borderRight: '0',
                        },
                        th: {
                            backgroundColor: '#ffffff',
                            padding: '4px 8px',
                            height: '32px',
                            fontWeight: 'bold',
                        },
                    },
                }}
                muiTableBodyCellProps={{
                    sx: {
                        padding: '4px 8px',
                        borderBottom: '1px solid #e5e7eb',
                        verticalAlign: 'middle',
                        backgroundColor: '#ffffff',
                    }
                }}
                muiTablePaperProps={{
                    sx: {
                        boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        backgroundColor: '#ffffff',
                    }
                }}
                renderDetailPanel={({ row }) => {
                    const product = row.original;
                    return (
                        <div className="p-0 ml-8" onClick={(e) => e.stopPropagation()}>
                            <MaterialReactTable
                                columns={subGridColumns}
                                data={product.recommendations}
                                muiTablePaperProps={{
                                    elevation: 0,
                                    sx: {
                                        margin: '0',
                                        boxShadow: 'none',
                                        backgroundColor: '#ffffff',
                                    }
                                }}
                                muiTableContainerProps={{
                                    sx: {
                                        '& .MuiTable-root': {
                                            borderCollapse: 'collapse',
                                            borderSpacing: '0',
                                            marginTop: '0',
                                            marginBottom: '0',
                                        }
                                    }
                                }}
                                muiTableBodyRowProps={() => ({
                                    sx: {
                                        cursor: 'pointer',
                                        height: '32px',
                                        backgroundColor: '#ffffff',
                                        '&:hover': {
                                            backgroundColor: '#f9f9f9',
                                        }
                                    },
                                })}
                                enableBottomToolbar={false}
                                enableTopToolbar={false}
                                enableColumnActions={false}
                                enableRowSelection={false}
                                enableSelectAll={false}
                                enableSorting={false}
                                enableGlobalFilter={false}
                                enableColumnFilters={false}
                                muiTableHeadProps={{
                                    sx: {
                                        th: {
                                            backgroundColor: '#ffffff',
                                            borderBottom: '1px solid #e5e7eb',
                                            padding: '3px 8px',
                                            height: '28px'
                                        },
                                    },
                                }}
                                muiTableBodyCellProps={{
                                    sx: {
                                        padding: '3px 8px',
                                        borderBottom: '1px solid #e5e7eb',
                                        verticalAlign: 'middle',
                                        backgroundColor: '#ffffff',
                                    }
                                }}
                            />
                        </div>
                    );
                }}
            />
        </div>
    );
};

export default InventoryAccordionTable;